import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Server, MapPin, Zap, Home as HomeIcon, Factory, Sprout, ArrowRight, ArrowUpRight, Gauge } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { IMAGES, SERVICES, POWER_INTEL, statusColor, PIPELINE } from "@/data/site";
import { Reveal, Overline } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

const ICONS = { Server, MapPin, Zap, Home: HomeIcon, Factory, Sprout };

const STATS = [
  { value: "300+ MW", label: "Path to Power goal" },
  { value: "≈800 acres", label: "Project area under evaluation" },
  { value: "Southeast", label: "Regional development focus" },
  { value: "6 Sectors", label: "Data, power, housing, agri-tech & more" },
];

export default function Home() {
  useSeo(
    "12 Stone Development Group | Data Center & Infrastructure Development",
    "12 Stone Development Group identifies, controls, entitles and develops strategic sites for data centers, power-intensive infrastructure, workforce housing and technology-driven growth across the Southeast."
  );
  const [featured, setFeatured] = useState([]);
  const [pipeline, setPipeline] = useState(PIPELINE);
  useEffect(() => {
    api.get("/projects?featured=true").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
    api.get("/content/pipeline").then((r) => { if (r.data?.states?.length) setPipeline(r.data); }).catch(() => {});
  }, []);

  const imgFor = (p) => {
    const first = (p.images || [])[0];
    if (!first) return IMAGES.aerialLand;
    return first.startsWith("http") ? first : fileUrl(first);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroTransmission} alt="Transmission infrastructure at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-transparent to-primary/60" />
        </div>
        <div className="absolute inset-0 hero-grid opacity-[0.15]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <Overline className="mb-6">Data Center · Power · Land · Infrastructure</Overline>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl text-white text-balance">
              Building the Infrastructure Behind Tomorrow's Growth
            </h1>
            <p className="mt-7 text-base sm:text-xl text-gray-300 max-w-2xl leading-relaxed">
              12 Stone Development Group identifies, controls, entitles and develops strategic sites for
              data centers, infrastructure, housing and technology-driven growth.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold rounded-full px-7 h-12" data-testid="hero-cta-view-opportunities">
                <Link to="/projects">VIEW DEVELOPMENT OPPORTUNITIES <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-full px-7 h-12 border border-border" data-testid="hero-cta-submit-site">
                <Link to="/site-submission">SUBMIT A SITE</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-white hover:text-primary hover:bg-transparent font-semibold rounded-full px-4 h-12" data-testid="hero-cta-partner">
                <Link to="/partners">PARTNER WITH 12 STONE <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {STATS.map((s, i) => (
            <div key={i} className="px-5 py-8">
              <div className="font-display text-2xl sm:text-3xl font-extrabold text-foreground">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      {/* PIPELINE HIGHLIGHT */}
      <section className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-7">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-5">
            <div>
              <div className="font-display text-3xl sm:text-4xl font-extrabold">{pipeline.totals?.acres}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/60 mt-1">Assembled across {pipeline.totals?.parcels}</div>
            </div>
            <div className="hidden sm:block h-11 w-px bg-white/15" />
            <div>
              <div className="font-display text-3xl sm:text-4xl font-extrabold">{pipeline.totals?.powerAlt}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/60 mt-1">Potential power under review</div>
            </div>
            <div className="hidden sm:block h-11 w-px bg-white/15" />
            <div>
              <div className="font-display text-3xl sm:text-4xl font-extrabold">GA · AL</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/60 mt-1">Two-state portfolio</div>
            </div>
          </div>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold rounded-full px-7 shrink-0" data-testid="home-pipeline-cta">
            <Link to="/projects">VIEW PIPELINE <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <p className="text-[11px] leading-relaxed text-white/50">Preliminary potential capacity under review — not confirmed or committed utility capacity. {pipeline.disclaimer}</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <Reveal>
          <Overline>What We Do</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground max-w-2xl text-balance">
            Development where land, power and infrastructure intersect
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.title} delay={i * 0.06}>
                <Link to={s.to} data-testid={`service-card-${i}`} className="group block h-full rounded-xl border border-border bg-card p-7 hover:border-primary/40 hover:bg-muted transition-all duration-300">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  <div className="mt-5 flex items-center text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="ml-1.5 h-4 w-4" />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* METTER FIRST FEATURE */}
      <section className="relative overflow-hidden border-y border-border">
        <div className="absolute inset-0">
          <img src={IMAGES.aerialLand} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <Reveal className="max-w-2xl">
            <Overline>Featured Development · Metter, Georgia</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold text-white text-balance">Project Metter First</h2>
            <p className="mt-5 text-gray-300 leading-relaxed">
              A large-scale integrated development vision uniting data center infrastructure, workforce housing,
              agri-tech, hydroponics, utility modernization and community growth in a single coordinated master plan.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {["300+ MW — Path to Power goal", "Fiber Backbone", "Heat Reuse Loop", "Workforce Housing"].map((t) => (
                <span key={t} className="font-mono text-xs px-3 py-1.5 rounded-full border border-border bg-white/5 text-gray-300">{t}</span>
              ))}
            </div>
            <Button asChild size="lg" className="mt-9 bg-accent hover:bg-accent/90 text-primary font-semibold rounded-full px-7" data-testid="home-metter-cta">
              <Link to="/metter-first">EXPLORE PROJECT METTER FIRST <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <Reveal className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <Overline>Development Pipeline</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground">Select Opportunities</h2>
            </div>
            <Link to="/projects" className="text-sm text-primary hover:underline flex items-center">View all projects <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={p.slug === "project-metter-first" ? "/metter-first" : `/projects/${p.slug}`} data-testid={`featured-project-${i}`} className="group block rounded-xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-all">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={imgFor(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block font-mono text-[10px] px-2.5 py-1 rounded-full border ${statusColor(p.status)}`}>{p.status}</span>
                    <h3 className="mt-3 font-display text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{p.location} · {p.acreage}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* POWER INTELLIGENCE */}
      <section className="border-t border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Power Infrastructure Intelligence</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground text-balance">
              We evaluate power before the market moves
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              12 Stone evaluates the power delivery pathways that determine whether a site can support large,
              high-density technology loads.
            </p>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {POWER_INTEL.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Button asChild size="lg" className="mt-9 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-7" data-testid="home-power-optimizer">
              <Link to="/data-centers"><Gauge className="mr-2 h-4 w-4" /> LAUNCH POWER OPTIMIZER</Link>
            </Button>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-xl overflow-hidden border border-border">
              <img src={IMAGES.substationNight} alt="Substation at night" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="relative rounded-2xl overflow-hidden border border-border">
          <img src={IMAGES.dataCenter} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/90" />
          <div className="relative p-10 sm:p-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white max-w-2xl mx-auto text-balance">
              Have land, capital or a project to advance?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Whether you control land, operate data centers, deliver power or invest in infrastructure — start a conversation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold rounded-full px-7"><Link to="/site-submission">SUBMIT A SITE</Link></Button>
              <Button asChild size="lg" className="bg-white/10 hover:bg-white/15 text-white border border-border font-semibold rounded-full px-7"><Link to="/partners">PARTNER WITH 12 STONE</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
